/**
 * GPSJam GPS/GNSS interference detection data source.
 *
 * Fetches daily H3 hex grid data from gpsjam.org static CSV files.
 * Each row contains an H3 resolution-4 cell with good/bad aircraft counts.
 * We convert the bad percentage into interference severity levels and
 * resolve H3 cell centers for map placement.
 *
 * Data endpoint: https://gpsjam.org/data/{YYYY-MM-DD}-h3_4.csv
 * Manifest:      https://gpsjam.org/data/manifest.csv
 * No API key required — static files, updated daily after midnight UTC.
 */

import { cellToLatLng } from 'h3-js';
import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, Severity } from '../base-source';
import { djb2Hash } from '@/utils/hash';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'gpsjam';

/** Minimum interference percentage to report (filters out noise). */
const MIN_INTERFERENCE_PCT = 2;

/**
 * GPSJam's interference formula (from their FAQ):
 * percent_bad = 100 * (count_bad - 1) / (count_good + count_bad)
 * Subtracts 1 from bad count to reduce false positives in low-traffic cells.
 */
function computeInterferencePct(countGood: number, countBad: number): number {
  const total = countGood + countBad;
  if (total === 0 || countBad <= 1) return 0;
  return 100 * (countBad - 1) / total;
}

function interferenceToSeverity(pct: number): Severity {
  if (pct >= 50) return 'critical';
  if (pct >= 25) return 'high';
  if (pct >= 10) return 'medium';
  if (pct >= 2) return 'low';
  return 'info';
}

function interferenceLabel(pct: number): string {
  if (pct >= 50) return 'Severe interference';
  if (pct >= 25) return 'Heavy interference';
  if (pct >= 10) return 'Moderate interference';
  if (pct >= 2) return 'Light interference';
  return 'Minimal interference';
}

/** Color for the interference level (matches GPSJam's scheme). */
function interferenceColor(pct: number): string {
  if (pct >= 10) return '#ef4444'; // Red
  if (pct >= 2) return '#f59e0b';  // Yellow/amber
  return '#22c55e';                 // Green
}

interface HexRow {
  hex: string;
  countGood: number;
  countBad: number;
}

/** Parse GPSJam CSV data into structured rows. */
function parseCSV(csv: string): HexRow[] {
  const lines = csv.trim().split('\n');
  if (lines.length < 2) return [];

  // Skip header: "hex,count_good_aircraft,count_bad_aircraft"
  const rows: HexRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const parts = lines[i].split(',');
    if (parts.length < 3) continue;

    const hex = parts[0].trim();
    const countGood = parseInt(parts[1], 10);
    const countBad = parseInt(parts[2], 10);

    if (!hex || isNaN(countGood) || isNaN(countBad)) continue;
    rows.push({ hex, countGood, countBad });
  }
  return rows;
}

/** Get the most recent available date (yesterday UTC, since today's data isn't ready yet). */
function getLatestDate(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

/** Cache the last fetched date to avoid re-fetching the same data. */
let lastFetchedDate = '';
let cachedEvents: NormalizedEvent[] = [];

export const gpsjamSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'GPSJam',
    description: 'GPS/GNSS interference detection via ADS-B analysis (H3 hex grid)',
    icon: 'radio',
    category: 'infrastructure',
    updateInterval: 900_000, // 15 min — data only updates daily, no need to poll often
    requiresApiKey: false,
    dataLicense: 'GPSJam / ADS-B Exchange',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[GPSJam] Interference source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
    cachedEvents = [];
    lastFetchedDate = '';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    const targetDate = getLatestDate();

    // Return cached data if we already fetched today's file
    if (targetDate === lastFetchedDate && cachedEvents.length > 0) {
      return cachedEvents;
    }

    try {
      const base = getApiBase();

      // gpsjam.org has a 2-3 day processing delay — try up to 3 days back
      // to find the most recent published data
      let csv: string | null = null;
      let usedDate = targetDate;

      for (let i = 0; i <= 3; i++) {
        const tryDate = new Date(targetDate);
        tryDate.setUTCDate(tryDate.getUTCDate() - i);
        const dateStr = tryDate.toISOString().slice(0, 10);

        const response = await globalThis.fetch(
          `${base}/api/data/gpsjam?date=${dateStr}`,
          { signal: AbortSignal.timeout(20_000) },
        );

        if (response.ok) {
          csv = await response.text();
          usedDate = dateStr;
          if (i > 0) console.info(`[GPSJam] Latest available data: ${dateStr} (${i}d processing delay)`);
          break;
        }
        if (response.status !== 404) {
          throw new Error(`GPSJam returned ${response.status}`);
        }
      }

      if (!csv) {
        throw new Error(`GPSJam: no data within 3 days of ${targetDate}`);
      }

      const events = processCSV(csv, usedDate);

      // Cache results
      lastFetchedDate = targetDate;
      cachedEvents = events;

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);

      // Return stale cache if available
      if (cachedEvents.length > 0) {
        console.warn('[GPSJam] Fetch failed, returning stale cache');
        return cachedEvents;
      }
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};

/** Process CSV text into NormalizedEvents, filtering to significant interference only. */
function processCSV(csv: string, date: string): NormalizedEvent[] {
  const rows = parseCSV(csv);
  const events: NormalizedEvent[] = [];
  const timestamp = new Date(date).toISOString();

  for (const row of rows) {
    const pct = computeInterferencePct(row.countGood, row.countBad);
    if (pct < MIN_INTERFERENCE_PCT) continue;

    let lat: number;
    let lng: number;
    try {
      const [cellLat, cellLng] = cellToLatLng(row.hex);
      lat = cellLat;
      lng = cellLng;
    } catch {
      // Invalid H3 index — skip
      continue;
    }

    const total = row.countGood + row.countBad;
    const label = interferenceLabel(pct);
    const roundedPct = Math.round(pct * 10) / 10;

    events.push({
      id: `gpsjam-${djb2Hash(row.hex + date)}`,
      source: SOURCE_ID,
      timestamp,
      type: 'gps_jamming',
      title: `${label} (${roundedPct}%)`,
      description: `${row.countBad} of ${total} aircraft reporting degraded GPS in this area`,
      severity: interferenceToSeverity(pct),
      location: {
        lat,
        lng,
        name: `GPS jamming zone`,
      },
      tags: ['gpsjam', 'gps-interference', 'gnss', 'electronic-warfare'],
      raw: {
        h3: row.hex,
        interferencePercent: roundedPct,
        countGood: row.countGood,
        countBad: row.countBad,
        totalAircraft: total,
        color: interferenceColor(pct),
        date,
      },
    });
  }

  console.info(`[GPSJam] Processed ${rows.length} hexes, ${events.length} with interference ≥${MIN_INTERFERENCE_PCT}%`);
  return events;
}
